import { z } from "zod";
/*
 * Session replay and the activity log ("Sessões"). The Tax Operations web app
 * records the operator's screen with rrweb (inputs masked, password, token and
 * key fields blocked) and uploads it in chunks. The admin API stores each
 * chunk gzipped in a private GCS bucket (GCP only, never AWS) with a lifecycle
 * retention, and keeps the session metadata in Postgres under tenant RLS.
 * Platform administrators (ADMIN:TAX_PLATFORM:ALL) watch a session next to
 * the timeline of what the operator did: calculations, lots, uploads, profile
 * confirmations, proposals, evidence actions, API and front-end errors.
 * Every replay read is written to the audit log.
 */
export const SESSION_REPLAY_LIMITS = {
    /** Bytes of the JSON-serialized `events` array of one chunk (before gzip). */
    maxChunkBytes: 512 * 1024,
    maxEventsPerChunk: 500,
    maxSignalsPerChunk: 50,
    /** The recorder seals a chunk at least this often while events are queued. */
    flushIntervalMs: 10_000,
    /** Chunks one replay session may hold; later chunks are refused. */
    maxChunksPerSession: 2_000,
    /** Chunk uploads one principal may make per minute (per API instance). */
    chunksPerPrincipalPerMinute: 60,
    /** Chunk writes one API instance accepts per minute, across principals. */
    chunksPerProcessPerMinute: 600,
    /** A chunk older than this, or this far in the future, is refused. */
    maxStartedAtAgeMs: 24 * 60 * 60_000,
    maxStartedAtFutureMs: 5 * 60_000,
    /** A replay session id rotates after this long without activity. */
    idleRotationMs: 30 * 60_000,
    /** Request body limit of the chunk upload route. */
    bodyLimitBytes: 1024 * 1024,
    /** Chunks one events page returns (bounded response size). */
    eventsPageMaxChunks: 25,
    defaultRetentionDays: 30,
    minRetentionDays: 1,
    maxRetentionDays: 365,
};
/** The notice text version the operator acknowledges; bump it when the copy changes. */
export const SESSION_REPLAY_NOTICE_VERSION = "2026-10-06";
export const replaySessionIdSchema = z.string().uuid();
/** A pathname without query or hash (the recorder never sends either). */
const routeSchema = z
    .string()
    .max(512)
    .regex(/^\/[^?#\s]*$/u);
/** Same shape as a sanitized transport id (X-Request-Id / X-Correlation-Id). */
const transportIdSchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u);
/* -- Ingestion (any signed-in operator, for their own session) ------------------- */
/** Client-side signals sent with a chunk: what the browser saw that the API did not. */
export const sessionReplaySignalKindSchema = z.enum([
    /** window.onerror */
    "CLIENT_ERROR",
    /** unhandledrejection */
    "UNHANDLED_REJECTION",
    /** A Tax API request that failed before an answer (offline, timeout, gateway deadline). */
    "NETWORK_ERROR",
    /** A client route change. */
    "NAVIGATION",
]);
export const sessionReplaySignalSchema = z
    .object({
    kind: sessionReplaySignalKindSchema,
    occurredAt: z.string().datetime(),
    route: routeSchema.optional(),
    /** Scrubbed on the client and again on the server (no tokens, e-mails, long digit runs). */
    message: z.string().max(500).optional(),
    /** Script URL without query or hash. */
    source: z.string().max(300).optional(),
    line: z.number().int().nonnegative().max(10_000_000).optional(),
    column: z.number().int().nonnegative().max(10_000_000).optional(),
    stack: z.string().max(2_000).optional(),
    /** NETWORK_ERROR: the request the API never answered. */
    requestId: transportIdSchema.optional(),
    method: z.enum(["GET", "POST", "PUT", "PATCH", "DELETE"]).optional(),
    /** NETWORK_ERROR: the API path (no query). */
    path: routeSchema.optional(),
})
    .strict();
export const sessionReplayViewportSchema = z
    .object({
    width: z.number().int().min(1).max(20_000),
    height: z.number().int().min(1).max(20_000),
})
    .strict();
export const sessionReplayChunkRequestSchema = z
    .object({
    replaySessionId: replaySessionIdSchema,
    /** Idempotency: a retried chunk carries the same id and is stored once. */
    chunkId: z.string().uuid(),
    sequence: z
        .number()
        .int()
        .min(0)
        .max(SESSION_REPLAY_LIMITS.maxChunksPerSession - 1),
    startedAt: z.string().datetime(),
    endedAt: z.string().datetime(),
    route: routeSchema.optional(),
    buildVersion: z.string().min(1).max(64).optional(),
    locale: z.string().min(2).max(35).optional(),
    timeZone: z.string().min(1).max(64).optional(),
    viewport: sessionReplayViewportSchema.optional(),
    /** rrweb events, opaque to the API beyond their size. */
    events: z.array(z.record(z.string(), z.unknown())).max(SESSION_REPLAY_LIMITS.maxEventsPerChunk),
    signals: z
        .array(sessionReplaySignalSchema)
        .max(SESSION_REPLAY_LIMITS.maxSignalsPerChunk)
        .default([]),
})
    .strict()
    .refine((chunk) => chunk.events.length > 0 || chunk.signals.length > 0, {
    message: "A chunk carries at least one event or signal",
    path: ["events"],
});
export const sessionReplayChunkResponseSchema = z
    .object({
    /** False when recording is off for this session (kill switch, settings or sampling). */
    accepted: z.boolean(),
    /** True when this chunk id was already stored (a retry). */
    duplicate: z.boolean(),
})
    .strict();
/** What the recorder needs to know before it loads rrweb. */
export const sessionReplayConfigSchema = z
    .object({
    /** Screen recording: kill switch, storage, organization setting and per-user exclusion combined. */
    enabled: z.boolean(),
    /**
     * Front-end error and navigation signals for the activity log. Only the deploy kill
     * switch turns them off; with recording off they are sent as signal-only chunks.
     */
    signalsEnabled: z.boolean(),
    sampleRate: z.number().min(0).max(1),
    maxChunkBytes: z.number().int().positive(),
    maxEventsPerChunk: z.number().int().positive(),
    flushIntervalMs: z.number().int().positive(),
    idleRotationMs: z.number().int().positive(),
    retentionDays: z.number().int().positive(),
    noticeVersion: z.string().min(1).max(40),
})
    .strict();
/* -- Settings (administrators) ---------------------------------------------------- */
export const SESSION_REPLAY_EXCLUDED_MAX = 200;
export const sessionReplaySettingsSchema = z
    .object({
    /** Organization switch. The deploy kill switch (SESSION_REPLAY_ENABLED) still wins. */
    enabled: z.boolean(),
    sampleRate: z.number().min(0).max(1),
    /** Principals never recorded (their activity log is still kept). */
    excludedPrincipalIds: z.array(z.string().uuid()).max(SESSION_REPLAY_EXCLUDED_MAX),
    /** Read-only: the deploy kill switch and whether replay storage is configured. */
    deployEnabled: z.boolean(),
    storageConfigured: z.boolean(),
    retentionDays: z.number().int().positive(),
    updatedAt: z.string().datetime().nullable(),
    updatedBy: z.string().max(320).nullable(),
})
    .strict();
export const sessionReplaySettingsUpdateSchema = z
    .object({
    enabled: z.boolean(),
    sampleRate: z.number().min(0).max(1),
    excludedPrincipalIds: z
        .array(z.string().uuid())
        .max(SESSION_REPLAY_EXCLUDED_MAX)
        .transform((ids) => [...new Set(ids)]),
})
    .strict();
/* -- Activity log ------------------------------------------------------------------ */
/** What an operator did, as one timeline. API rows come from the request; client rows from signals. */
export const sessionActivityKindSchema = z.enum([
    /** An individual operation calculation (POST /v1/operations/simulate). */
    "CALCULATION",
    /** A lot (upload session, tasks, coverage). */
    "LOT",
    /** A lot simulation (POST /v1/calculations/simulate). */
    "SIMULATION",
    "RECALCULATION",
    /** Workbook inspect or repair suggestions. */
    "UPLOAD",
    /** Workbook import. */
    "IMPORT",
    /** "Confirmar perfil" input corrections. */
    "PROFILE_CONFIRMATION",
    "PROPOSAL",
    "EVIDENCE",
    /** Any other API request that answered 4xx/5xx. */
    "API_ERROR",
    /** window.onerror, unhandled rejections and requests that never got an answer. */
    "CLIENT_ERROR",
    "NAVIGATION",
    "OTHER",
]);
export const sessionActivityOutcomeSchema = z.enum(["OK", "ERROR"]);
export const sessionActivityItemSchema = z
    .object({
    id: z.string().uuid(),
    occurredAt: z.string().datetime(),
    replaySessionId: replaySessionIdSchema.nullable(),
    principalId: z.string().uuid(),
    principalEmail: z.string().max(320).nullable().optional(),
    kind: sessionActivityKindSchema,
    /** API: the route template ("POST /v1/calculations/:uploadId/tasks"); client: the signal kind. */
    action: z.string().min(1).max(160),
    outcome: sessionActivityOutcomeSchema,
    httpStatus: z.number().int().min(100).max(599).nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    route: z.string().max(512).nullable(),
    requestId: z.string().max(128).nullable(),
    correlationId: z.string().max(128).nullable(),
    problemCode: z.string().max(64).nullable(),
    /** Scrubbed message (client errors) or problem title. */
    message: z.string().max(500).nullable(),
    subject: z
        .object({
        type: z.string().min(1).max(40),
        id: z.string().min(1).max(128),
    })
        .strict()
        .nullable(),
    /** The failed-upload support code recorded for the same request, when there is one. */
    uploadFailureId: z.string().max(128).nullable(),
    /** Audit actions written by the same request (e.g. calculation.completed). */
    auditActions: z.array(z.string().max(120)).max(20),
})
    .strict();
export const SESSION_ACTIVITY_PAGE_MAX = 500;
export const sessionActivityPageSchema = z
    .object({
    items: z.array(sessionActivityItemSchema),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
})
    .strict();
export const sessionActivityQuerySchema = z
    .object({
    /** Principal id or e-mail (substring, case-insensitive). */
    principal: z.string().trim().min(1).max(320).optional(),
    replaySessionId: replaySessionIdSchema.optional(),
    kind: sessionActivityKindSchema.optional(),
    outcome: sessionActivityOutcomeSchema.optional(),
    from: z.string().date().optional(),
    to: z.string().date().optional(),
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(SESSION_ACTIVITY_PAGE_MAX).optional(),
})
    .strict();
/* -- Sessions (administrators) ---------------------------------------------------- */
export const sessionReplayPrincipalSchema = z
    .object({
    principalId: z.string().uuid(),
    email: z.string().max(320).nullable(),
    displayName: z.string().max(200).nullable(),
})
    .strict();
export const sessionReplaySummarySchema = z
    .object({
    id: replaySessionIdSchema,
    organizationId: z.string().uuid(),
    principal: sessionReplayPrincipalSchema,
    startedAt: z.string().datetime(),
    endedAt: z.string().datetime(),
    durationMs: z.number().int().nonnegative(),
    firstRoute: z.string().max(512).nullable(),
    lastRoute: z.string().max(512).nullable(),
    chunkCount: z.number().int().nonnegative(),
    eventCount: z.number().int().nonnegative(),
    /** Stored (gzipped) bytes. */
    byteSize: z.number().int().nonnegative(),
    clientErrorCount: z.number().int().nonnegative(),
    apiErrorCount: z.number().int().nonnegative(),
    actionCount: z.number().int().nonnegative(),
    /** Distinct activity kinds seen in the session. */
    actionKinds: z.array(sessionActivityKindSchema),
    userAgent: z.string().max(400).nullable(),
    buildVersion: z.string().max(64).nullable(),
    expiresAt: z.string().datetime(),
})
    .strict();
export const sessionReplayChunkMetaSchema = z
    .object({
    sequence: z.number().int().nonnegative(),
    startedAt: z.string().datetime(),
    endedAt: z.string().datetime(),
    eventCount: z.number().int().nonnegative(),
    byteSize: z.number().int().nonnegative(),
    route: z.string().max(512).nullable(),
})
    .strict();
export const sessionReplayDetailSchema = sessionReplaySummarySchema
    .extend({
    locale: z.string().max(35).nullable(),
    timeZone: z.string().max(64).nullable(),
    viewport: sessionReplayViewportSchema.nullable(),
    routes: z.array(z.string().max(512)).max(200),
    chunks: z.array(sessionReplayChunkMetaSchema).max(SESSION_REPLAY_LIMITS.maxChunksPerSession),
})
    .strict();
export const SESSION_REPLAY_PAGE_MAX = 100;
export const sessionReplayPageSchema = z
    .object({
    items: z.array(sessionReplaySummarySchema),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
})
    .strict();
export const sessionReplayQuerySchema = z
    .object({
    /** Principal id or e-mail (substring, case-insensitive). */
    principal: z.string().trim().min(1).max(320).optional(),
    /** One organization in the caller's scope; default: every organization in scope. */
    organizationId: z.string().uuid().optional(),
    from: z.string().date().optional(),
    to: z.string().date().optional(),
    hasErrors: z.enum(["true", "false"]).optional(),
    actionKind: sessionActivityKindSchema.optional(),
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(SESSION_REPLAY_PAGE_MAX).optional(),
})
    .strict();
export const sessionReplayEventsQuerySchema = z
    .object({
    fromSequence: z.coerce
        .number()
        .int()
        .min(0)
        .max(SESSION_REPLAY_LIMITS.maxChunksPerSession)
        .default(0),
    maxChunks: z.coerce
        .number()
        .int()
        .min(1)
        .max(SESSION_REPLAY_LIMITS.eventsPageMaxChunks)
        .default(10),
})
    .strict();
export const sessionReplayEventsPageSchema = z
    .object({
    events: z.array(z.record(z.string(), z.unknown())),
    /** The next sequence to request, null when every stored chunk was returned. */
    nextSequence: z.number().int().nonnegative().nullable(),
})
    .strict();
/** Find the session behind a request, an upload failure or a subject (a lot, a calculation). */
export const sessionReplayLookupQuerySchema = z
    .object({
    requestId: transportIdSchema.optional(),
    correlationId: transportIdSchema.optional(),
    uploadFailureId: transportIdSchema.optional(),
    subjectId: z.string().min(1).max(128).optional(),
})
    .strict()
    .refine((query) => Object.values(query).some((value) => value !== undefined), {
    message: "Give a requestId, correlationId, uploadFailureId or subjectId",
    path: ["requestId"],
});
export const sessionReplayLookupSchema = z
    .object({
    /** The session the activity belongs to, null when it was not recorded. */
    replaySessionId: replaySessionIdSchema.nullable(),
    /** When the matching activity happened (to seek the replay there). */
    occurredAt: z.string().datetime().nullable(),
    /** True when the session has stored chunks and has not expired. */
    recorded: z.boolean(),
})
    .strict();
/** Header the web client sends so the API can tie each request to the recorded session. */
export const SESSION_REPLAY_HEADER = "X-Tax-Session-Id";
//# sourceMappingURL=session-replay.js.map